import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { authService } from '@/lib/api';

// Define the user type
interface User {
  _id: string;
  id?: string;
  email: string;
  firstName: string;
  lastName: string;
  name: string;
  role: 'athlete' | 'coach' | 'admin';
  isEmailVerified: boolean;
  coaches?: Coach[];
  primaryCoachId?: string;
  coachId?: string | null;
  athletes?: string[];
  bio?: string;
  experience?: string;
  specialties?: string[];
  qualifications?: Qualification[];
  avatarUrl?: string;
  socialLinks?: SocialLinks;
}

interface Coach {
  _id: string;
  id?: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'coach';
  bio?: string;
  experience?: string;
  specialties?: string[];
  qualifications?: Qualification[];
  avatarUrl?: string;
  socialLinks?: SocialLinks;
}

interface Qualification {
  title: string;
  institution?: string;
  year?: string;
}

interface SocialLinks {
  website?: string;
  linkedin?: string;
  instagram?: string;
  twitter?: string;
  facebook?: string;
  youtube?: string;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  role: 'athlete' | 'coach';
}

interface ProfileUpdateData {
  firstName?: string;
  lastName?: string;
  bio?: string;
  experience?: string;
  specialties?: string[];
  qualifications?: Qualification[];
  avatarUrl?: string;
  socialLinks?: SocialLinks;
}

// Define the expected response type for the register function
interface RegisterResponse {
  isExistingUser?: boolean;
  verificationToken?: string;
  user?: User;
  token?: string;
}

// Define the auth context type
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (userData: RegisterData) => Promise<RegisterResponse | void>;
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;
  updateProfile: (data: ProfileUpdateData) => Promise<void>;
  deleteAccount: () => Promise<void>;
}

// Define props for AuthProvider
interface AuthProviderProps {
  children: React.ReactNode;
}

// Default context with empty implementations
const defaultContext: AuthContextType = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  login: async () => {},
  logout: async () => {},
  register: async () => { return undefined; },
  verifyEmail: async () => {},
  resendVerification: async () => {},
  forgotPassword: async () => {},
  resetPassword: async () => {},
  updateProfile: async () => {},
  deleteAccount: async () => {}
};

// Create the context
const AuthContext = createContext<AuthContextType>(defaultContext);

// Provider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        setLoading(true);
        console.log('🔍 Starting authentication check...');
        
        // Check localStorage for token first
        const hasToken = localStorage.getItem('token');
        console.log('🔑 Token in localStorage:', {
          exists: !!hasToken,
          length: hasToken?.length,
          firstChars: hasToken ? `${hasToken.substring(0, 10)}...` : null
        });
        
        // Try to refresh the token if it exists
        if (hasToken) {
          try {
            console.log('🔄 Attempting token refresh...');
            const response = await api.get('/auth/check');
            console.log('🔄 Auth check response:', response.data);
            
            if (response.data?.status === 'success' && response.data?.data?.user) {
              const userData = response.data.data.user;
              console.log('✅ Auth check successful, user data:', {
                id: userData._id,
                email: userData.email,
                role: userData.role,
                isVerified: userData.isEmailVerified
              });
              
              setCurrentUser({
                ...userData,
                _id: userData._id,
                id: userData.id || userData._id,
                name: `${userData.firstName} ${userData.lastName}`,
                email: userData.email,
                firstName: userData.firstName,
                lastName: userData.lastName,
                role: userData.role,
                isEmailVerified: userData.isEmailVerified,
                coaches: userData.coaches,
                primaryCoachId: userData.primaryCoachId,
                athletes: userData.athletes,
                bio: userData.bio,
                experience: userData.experience,
                specialties: userData.specialties,
                qualifications: userData.qualifications,
                avatarUrl: userData.avatarUrl,
                socialLinks: userData.socialLinks
              });
              setIsAuthenticated(true);
            } else {
              console.log('❌ Auth check failed - no valid user data');
              localStorage.removeItem('token');
              setCurrentUser(null);
              setIsAuthenticated(false);
            }
          } catch (err) {
            console.error('❌ Auth check failed:', {
              error: err,
              message: err.message,
              response: err.response?.data
            });
            localStorage.removeItem('token');
            setCurrentUser(null);
            setIsAuthenticated(false);
          }
        } else {
          console.log('❌ No token found in localStorage');
          setCurrentUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('❌ Error in auth check:', error);
        setError(error.message);
        setCurrentUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    // Prevent multiple login attempts
    if (loading) {
      console.log('⚠️ Login already in progress, skipping duplicate attempt');
      return;
    }

    try {
      console.log('🔑 Starting login process...', { 
        email,
        localStorage: {
          hasToken: !!localStorage.getItem('token'),
          allKeys: Object.keys(localStorage)
        },
        sessionStorage: {
          allKeys: Object.keys(sessionStorage)
        },
        cookies: document.cookie,
        currentPath: window.location.pathname
      });
      
      setError(null);
      setLoading(true);
      
      const response = await authService.login({ email, password });
      console.log('📥 Login response received:', {
        hasToken: !!response?.token,
        hasUserData: !!response?.data?.user,
        status: response?.status,
        userData: response?.data?.user,
        localStorage: {
          hasToken: !!localStorage.getItem('token'),
          tokenLength: localStorage.getItem('token')?.length,
          allKeys: Object.keys(localStorage)
        }
      });
      
      if (response?.token) {
        console.log('✅ Login successful, preparing to update state...');
        
        // Set user data
        if (response.data?.user) {
          console.log('👤 User data received:', {
            id: response.data.user.id || response.data.user._id,
            email: response.data.user.email,
            role: response.data.user.role,
            isVerified: response.data.user.isEmailVerified
          });
          
          const userData = {
            ...response.data.user,
            id: response.data.user.id || response.data.user._id,
            _id: response.data.user._id || response.data.user.id,
            email: response.data.user.email,
            role: response.data.user.role,
            isEmailVerified: response.data.user.isEmailVerified,
            coaches: response.data.user.coaches || [],
            primaryCoachId: response.data.user.primaryCoachId,
            athletes: response.data.user.athletes || [],
            bio: response.data.user.bio,
            experience: response.data.user.experience,
            specialties: response.data.user.specialties,
            qualifications: response.data.user.qualifications,
            avatarUrl: response.data.user.avatarUrl,
            socialLinks: response.data.user.socialLinks
          };

          console.log('🔄 Updating state with user data...');
          setCurrentUser(userData);
          setIsAuthenticated(true);
          
          console.log('✅ State updated, current state:', {
            hasUser: !!userData,
            isAuthenticated: true,
            userData,
            localStorage: {
              hasToken: !!localStorage.getItem('token'),
              tokenLength: localStorage.getItem('token')?.length,
              allKeys: Object.keys(localStorage)
            }
          });
          
          // Add a delay before navigation to allow logs to be visible
          console.log('⏳ Waiting 3 seconds before navigation...');
          await new Promise(resolve => setTimeout(resolve, 3000));
          
          console.log('🔄 Navigating to app...');
          navigate('/app', { replace: true });
          return;
        }
      }
      
      throw new Error('Login failed: Invalid response format');
    } catch (error: any) {
      console.error('❌ Login error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        localStorage: {
          hasToken: !!localStorage.getItem('token'),
          allKeys: Object.keys(localStorage)
        },
        sessionStorage: {
          allKeys: Object.keys(sessionStorage)
        },
        cookies: document.cookie,
        currentPath: window.location.pathname
      });
      setError(error.response?.data?.message || 'Login failed');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      localStorage.removeItem('token');
      setCurrentUser(null);
      setIsAuthenticated(false);
      navigate('/auth/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local state even if the API call fails
      localStorage.removeItem('token');
      setCurrentUser(null);
      setIsAuthenticated(false);
      navigate('/auth/login');
    }
  };

  const register = async (userData: RegisterData): Promise<RegisterResponse | void> => {
    try {
      setError(null);
      const response = await api.post<RegisterResponse>('/auth/register', userData);
      
      // Check if verification token is returned (for development)
      if (response.data && response.data.verificationToken) {
        console.log('Verification token received:', response.data.verificationToken);
        navigate(`/verify-email?token=${response.data.verificationToken}`);
      } else {
        // In production, redirect to waiting page
        navigate(`/verify-email?email=${encodeURIComponent(userData.email)}`);
      }
      return response.data;
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
        toast.error(error.message);
      } else {
        setError('An unexpected error occurred');
        toast.error('An unexpected error occurred');
      }
      throw error;
    }
  };

  const verifyEmail = async (token: string) => {
    // Implementation of verifyEmail function
  };

  const resendVerification = async () => {
    // Implementation of resendVerification function
  };

  const forgotPassword = async (email: string) => {
    // Implementation of forgotPassword function
  };

  const resetPassword = async (token: string, password: string) => {
    // Implementation of resetPassword function
  };

  const updateProfile = async (data: ProfileUpdateData) => {
    try {
      setError(null);
      const response = await api.patch<{ data: { user: User } }>('/auth/profile', data);
      setCurrentUser(response.data.data.user);
      toast.success('Profile updated successfully');
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
        toast.error(error.message);
      } else {
        setError('An unexpected error occurred');
        toast.error('An unexpected error occurred');
      }
      throw error;
    }
  };

  const deleteAccount = async () => {
    try {
      setError(null);
      await api.delete('/auth/delete-account');
      // Clear local storage and state
      localStorage.removeItem('token');
      setCurrentUser(null);
      setIsAuthenticated(false);
      navigate('/');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Failed to delete account. Please try again.');
      throw error;
    }
  };

  const value = {
    user: currentUser,
    isAuthenticated,
    isLoading: loading,
    error,
    login,
    logout,
    register,
    verifyEmail,
    resendVerification,
    forgotPassword,
    resetPassword,
    updateProfile,
    deleteAccount
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 